import type { ReactElement, ReactNode } from "react";
import { ErrorState } from "./state-components/error-state";
import { NotFoundState } from "./state-components/not-found-state";
import { usePageLayoutState } from "./hooks/use-page-layout-state";
import { PageHeader } from "./shared-components/page-header";
import { PageWrapper } from "./shared-components/page-wrapper";
import type { RequestState } from "@redux/state/shared-types";
import type { ErrorStateProps, NotFoundStateProps } from "./types";

export interface DataFormPageLayoutProps<T> {
  /** Current fetch state from Redux */
  fetching: RequestState;
  /** The data from Redux state */
  data: T | null | undefined;
  /** Page title */
  title: string;
  /** Page subtitle */
  subtitle?: string;
  /** Render function for success state */
  children: (data: T) => ReactNode;
  /** Loading component to display during pending state */
  loadingComponent: ReactNode;
  /** Props for error state */
  errorStateProps: ErrorStateProps;
  /** Props for not found state */
  notFoundStateProps: NotFoundStateProps;
  /** Additional className for the wrapper */
  className?: string;
}

/**
 * DataFormPageLayout - Layout for form pages that require data fetching
 *
 * Handles loading, error, not-found, and success states automatically.
 * Use this for edit/update forms that need to fetch existing data.
 * Unlike DetailPageLayout, it doesn't include back navigation.
 *
 * For simple form pages without data fetching (e.g., create forms, contact forms),
 * use FormPageLayout instead.
 *
 * @example
 * ```tsx
 * const MyEditPage = () => {
 *   const { data, fetching } = useAppSelector(selectData);
 *
 *   return (
 *     <DataFormPageLayout
 *       fetching={fetching}
 *       data={data}
 *       title="Mon profil"
 *       loadingComponent={
 *         <FormCard>
 *           <SchemaFormSkeleton schema={formSchema} />
 *         </FormCard>
 *       }
 *     >
 *       {(item) => <EditForm item={item} />}
 *     </DataFormPageLayout>
 *   );
 * };
 * ```
 */
export function DataFormPageLayout<T>({
  fetching,
  data,
  title,
  subtitle,
  children,
  loadingComponent,
  errorStateProps,
  notFoundStateProps,
  className,
}: DataFormPageLayoutProps<T>): ReactElement {
  const hasData = data !== null && data !== undefined;
  const { isLoading, isError, isSuccess } = usePageLayoutState({
    fetching,
    alwaysIdleAsLoading: true,
    keepDataWhileRefetching: true,
  });

  // Render loading state
  if (isLoading) {
    return (
      <PageWrapper className={className}>
        <PageHeader title={title} subtitle={subtitle} />
        {loadingComponent}
      </PageWrapper>
    );
  }

  // Render error state
  if (isError) {
    return (
      <PageWrapper className={className}>
        <PageHeader title={title} subtitle={subtitle} />
        <ErrorState {...errorStateProps} />
      </PageWrapper>
    );
  }

  // Render not found state (no data after success)
  if (isSuccess && !hasData) {
    return (
      <PageWrapper className={className}>
        <PageHeader title={title} subtitle={subtitle} />
        <NotFoundState {...notFoundStateProps} />
      </PageWrapper>
    );
  }

  // Render success state with data
  if (hasData) {
    return (
      <PageWrapper className={className}>
        <PageHeader title={title} subtitle={subtitle} />
        {children(data)}
      </PageWrapper>
    );
  }

  // Fallback
  return (
    <PageWrapper className={className}>
      <PageHeader title={title} subtitle={subtitle} />
      {loadingComponent}
    </PageWrapper>
  );
}
